import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArrowRight,
  ClipboardCheck,
  HeartHandshake,
  LogOut,
  Menu,
  MessageCircle,
  Sparkles,
  UserRound,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import heroImage from "../../assets/hero-mother-child.png";
import { useAuth } from "./auth";
import { AuthShell, FieldError, LogoMark } from "./common";
import { api, formatMoney, loginSchema, registerSchema, type PublicData, type Service, type User } from "./core";

export function SiteLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = [
    ["Beranda", "/"],
    ["Layanan", "/layanan"],
    ["Tes Psikologi", "/tes"],
    ["Edukasi", "/edukasi"],
    ["Tentang", "/tentang"],
    ["Kontak", "/kontak"],
  ];

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b bg-background/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
          <LogoMark />
          <nav className="hidden items-center gap-1 lg:flex">
            {nav.map(([label, href]) => (
              <NavLink
                key={href}
                to={href}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2 text-sm font-medium ${isActive ? "bg-secondary text-primary" : "text-muted-foreground hover:text-primary"}`
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden items-center gap-2 sm:flex">
            {user ? (
              <>
                <Button asChild variant="ghost" size="sm">
                  <Link to={user.role === "client" ? "/portal" : "/admin"}>{user.role === "client" ? "Portal" : "Admin"}</Link>
                </Button>
                <Button variant="outline" size="sm" onClick={logout}>
                  <LogOut />
                  Keluar
                </Button>
              </>
            ) : (
              <>
                <Button asChild variant="ghost" size="sm">
                  <Link to="/masuk">Masuk</Link>
                </Button>
                <Button asChild size="sm">
                  <Link to="/daftar">Daftar</Link>
                </Button>
              </>
            )}
          </div>
          <Button variant="outline" size="icon" className="lg:hidden" onClick={() => setOpen(value => !value)} aria-label="Menu">
            <Menu />
          </Button>
        </div>
        {open && (
          <div className="border-t px-4 py-3 lg:hidden">
            <div className="mx-auto grid max-w-7xl gap-1">
              {nav.map(([label, href]) => (
                <NavLink key={href} to={href} onClick={() => setOpen(false)} className="rounded-md px-3 py-2 text-sm font-medium">
                  {label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </header>
      <Outlet />
      <Footer />
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-t bg-secondary/40">
      <div className="mx-auto grid max-w-7xl gap-6 px-4 py-10 sm:px-6 md:grid-cols-[1.4fr_1fr_1fr] lg:px-8">
        <div>
          <LogoMark />
          <p className="mt-4 max-w-md text-sm leading-6 text-muted-foreground">
            Biro Psikologi MotherSense mendampingi ibu, perempuan, pasangan, dan keluarga melalui edukasi,
            konsultasi psikologis, dan asesmen yang direview profesional.
          </p>
        </div>
        <div>
          <h3 className="font-semibold text-primary">Layanan</h3>
          <div className="mt-3 grid gap-2 text-sm text-muted-foreground">
            <Link to="/layanan">Konsultasi psikologis</Link>
            <Link to="/tes">Tes IQ, minat bakat, kesiapan sekolah</Link>
            <Link to="/edukasi">Edukasi parenting dan relasi</Link>
          </div>
        </div>
        <div>
          <h3 className="font-semibold text-primary">Kontak</h3>
          <div className="mt-3 grid gap-2 text-sm text-muted-foreground">
            <a href="https://instagram.com/mothersense.id">@mothersense.id</a>
            <a href="https://wa.me/6281234567890">WhatsApp admin</a>
            <span>Biro Psikologi Mothersense</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function usePublicData() {
  const [data, setData] = useState<PublicData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api<PublicData>("/api/public")
      .then(setData)
      .catch(error => toast.error(error.message))
      .finally(() => setLoading(false));
  }, []);

  return { data, loading };
}

export function HomePage() {
  const { data } = usePublicData();
  const services = data?.services ?? [];

  return (
    <main>
      <section
        className="hero-shell"
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(42, 37, 32, 0.72), rgba(42, 37, 32, 0.28)), url(${heroImage})`,
        }}
      >
        <div className="mx-auto flex min-h-[74svh] max-w-7xl items-center px-4 py-20 sm:px-6 lg:px-8">
          <div className="max-w-2xl text-white">
            <Badge className="border-white/30 bg-white/15 text-white">Biro Psikologi Mothersense</Badge>
            <h1 className="mt-6 font-display text-5xl leading-[1.02] sm:text-6xl lg:text-7xl">MotherSense Indonesia</h1>
            <p className="mt-5 max-w-xl text-base leading-7 text-white/88 sm:text-lg">
              Ruang psikologis yang tenang untuk ibu, perempuan, pasangan, dan keluarga saat menata transisi, relasi,
              parenting, dan kebutuhan asesmen anak.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90">
                <Link to="/daftar">
                  Mulai konsultasi
                  <ArrowRight />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white/50 bg-white/10 text-white hover:bg-white/20">
                <a href="https://wa.me/6281234567890">
                  <MessageCircle />
                  WhatsApp admin
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-5 px-4 py-12 sm:px-6 md:grid-cols-3 lg:px-8">
        {[
          ["Ibu", "Transisi menjadi ibu, relasi setelah anak, dan pengembangan diri.", HeartHandshake],
          ["Keluarga", "Konsultasi pasangan dan keluarga dengan pendekatan psikologis.", Users],
          ["Anak", "Tes psikologi dan kesiapan sekolah dengan review profesional.", ClipboardCheck],
        ].map(([title, desc, Icon]) => (
          <div key={String(title)} className="rounded-lg border bg-card p-5">
            <Icon className="size-6 text-primary" />
            <h2 className="mt-4 font-display text-2xl text-primary">{title}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{desc}</p>
          </div>
        ))}
      </section>

      <section className="bg-secondary/45 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionTitle eyebrow="Layanan utama" title="Dukungan psikologis, asesmen, dan edukasi dalam satu platform." />
          <ServiceGrid services={services.slice(0, 4)} />
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
        <div>
          <SectionTitle eyebrow="Alur layanan" title="Pembayaran manual, asesmen online, review psikolog." />
          <p className="mt-4 text-muted-foreground">
            Platform menjaga proses tetap manusiawi: klien mengisi data dan tes secara online, admin memverifikasi
            pembayaran, psikolog meninjau jawaban, lalu laporan PDF dirilis saat siap.
          </p>
        </div>
        <div className="grid gap-4">
          {[
            ["Pilih layanan", "Klien memilih konsultasi atau tes psikologi dari portal."],
            ["Transfer bank", "Nomor rekening muncul setelah pesanan dibuat, lalu bukti transfer diunggah."],
            ["Review profesional", "Tes online tidak memberi skor instan. Hasil dirilis sebagai laporan PDF setelah review."],
          ].map(([title, desc], index) => (
            <div key={title} className="flex gap-4 rounded-lg border bg-card p-5">
              <span className="grid size-9 shrink-0 place-items-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                {index + 1}
              </span>
              <div>
                <h3 className="font-semibold text-primary">{title}</h3>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

function SectionTitle({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div>
      <p className="text-sm font-medium uppercase tracking-[0.2em] text-accent-foreground/70">{eyebrow}</p>
      <h2 className="mt-3 font-display text-3xl leading-tight text-primary sm:text-4xl">{title}</h2>
    </div>
  );
}

function ServiceGrid({ services }: { services: Service[] }) {
  return (
    <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
      {services.map(service => (
        <Card key={service.id} className="rounded-lg">
          <CardHeader>
            <Badge>{service.category}</Badge>
            <CardTitle className="font-display text-2xl text-primary">{service.title}</CardTitle>
            <CardDescription>{service.duration}</CardDescription>
          </CardHeader>
          <CardContent className="flex h-full flex-col gap-5">
            <p className="text-sm leading-6 text-muted-foreground">{service.description}</p>
            <div className="mt-auto flex items-center justify-between gap-3">
              <span className="font-semibold text-primary">{formatMoney.format(service.price)}</span>
              <Button asChild size="sm" variant="outline">
                <Link to="/daftar">Pesan</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function ServicesPage() {
  const { data } = usePublicData();
  return (
    <main className="page-shell">
      <SectionTitle eyebrow="Layanan" title="Konsultasi dan asesmen untuk kebutuhan keluarga." />
      <ServiceGrid services={data?.services ?? []} />
    </main>
  );
}

export function TestsPage() {
  const { data } = usePublicData();
  const tests = (data?.services ?? []).filter(service => service.category === "Tes Psikologi");
  return (
    <main className="page-shell">
      <SectionTitle eyebrow="Tes psikologi" title="Pilihan asesmen untuk anak dan dewasa." />
      <ServiceGrid services={tests} />
    </main>
  );
}

export function EducationPage() {
  const { data } = usePublicData();
  return (
    <main className="page-shell">
      <SectionTitle eyebrow="Edukasi" title="Catatan singkat untuk ibu, relasi, dan parenting." />
      <div className="mt-8 grid gap-5 md:grid-cols-3">
        {(data?.posts ?? []).map(post => (
          <article key={post.id} className="rounded-lg border bg-card p-6">
            <Badge>{post.category}</Badge>
            <h2 className="mt-4 font-display text-2xl text-primary">{post.title}</h2>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">{post.excerpt}</p>
          </article>
        ))}
      </div>
    </main>
  );
}

export function AboutPage() {
  return (
    <main className="page-shell">
      <div className="grid gap-10 lg:grid-cols-[1fr_1fr]">
        <SectionTitle eyebrow="Tentang" title="Biro Psikologi Mothersense untuk fase hidup perempuan dan keluarga." />
        <div className="space-y-4 text-sm leading-7 text-muted-foreground">
          <p>
            MotherSense berfokus pada edukasi masyarakat umum, konsultasi psikologis untuk ibu, pasangan, keluarga,
            perempuan pra-nikah, dan pengembangan layanan asesmen anak.
          </p>
        </div>
      </div>
    </main>
  );
}

export function ContactPage() {
  return (
    <main className="page-shell">
      <SectionTitle eyebrow="Kontak" title="Mulai dari pesan singkat atau daftar portal." />
      <div className="mt-8 grid gap-5 md:grid-cols-3">
        {[
          ["Instagram", "@mothersense.id", "https://instagram.com/mothersense.id", Sparkles],
          ["WhatsApp", "Hubungi admin", "https://wa.me/6281234567890", MessageCircle],
          ["Portal", "Daftar klien", "/daftar", UserRound],
        ].map(([title, label, href, Icon]) => (
          <a key={title} href={href} className="rounded-lg border bg-card p-6 transition hover:border-primary/40">
            <Icon className="size-6 text-primary" />
            <h2 className="mt-4 font-display text-2xl text-primary">{title}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{label}</p>
          </a>
        ))}
      </div>
    </main>
  );
}

export function LoginPage() {
  const { refresh, user } = useAuth();
  const navigate = useNavigate();
  const form = useForm<z.infer<typeof loginSchema>>({ resolver: zodResolver(loginSchema) });

  if (user) return <Navigate to={user.role === "client" ? "/portal" : "/admin"} replace />;

  const onSubmit = form.handleSubmit(async values => {
    try {
      const payload = await api<{ user: User }>("/api/auth/login", { method: "POST", body: JSON.stringify(values) });
      await refresh();
      navigate(payload.user.role === "client" ? "/portal" : "/admin");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Gagal masuk");
    }
  });

  return (
    <AuthShell title="Masuk" description="Gunakan email yang terdaftar di MotherSense.">
      <form onSubmit={onSubmit} className="grid gap-4">
        <FieldError message={form.formState.errors.email?.message}>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="email" {...form.register("email")} />
        </FieldError>
        <FieldError message={form.formState.errors.password?.message}>
          <Label htmlFor="password">Kata sandi</Label>
          <Input id="password" type="password" autoComplete="current-password" {...form.register("password")} />
        </FieldError>
        <Button type="submit" disabled={form.formState.isSubmitting}>
          Masuk
          <ArrowRight />
        </Button>
        <p className="text-sm text-muted-foreground">
          Belum punya akun?{" "}
          <Link to="/daftar" className="font-medium text-primary">
            Daftar di sini
          </Link>
          .
        </p>
      </form>
    </AuthShell>
  );
}

export function RegisterPage() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const form = useForm<z.infer<typeof registerSchema>>({ resolver: zodResolver(registerSchema) });

  const onSubmit = form.handleSubmit(async values => {
    try {
      await api<{ user: User }>("/api/auth/register", { method: "POST", body: JSON.stringify(values) });
      await refresh();
      navigate("/portal");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Gagal daftar");
    }
  });

  return (
    <AuthShell title="Daftar klien" description="Buat akun untuk memesan layanan, mengunggah pembayaran, dan menerima laporan.">
      <form onSubmit={onSubmit} className="grid gap-4">
        <FieldError message={form.formState.errors.name?.message}>
          <Label htmlFor="name">Nama lengkap</Label>
          <Input id="name" autoComplete="name" {...form.register("name")} />
        </FieldError>
        <FieldError message={form.formState.errors.email?.message}>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="email" {...form.register("email")} />
        </FieldError>
        <FieldError message={form.formState.errors.phone?.message}>
          <Label htmlFor="phone">Nomor WhatsApp</Label>
          <Input id="phone" autoComplete="tel" {...form.register("phone")} />
        </FieldError>
        <FieldError message={form.formState.errors.password?.message}>
          <Label htmlFor="password">Kata sandi</Label>
          <Input id="password" type="password" autoComplete="new-password" {...form.register("password")} />
        </FieldError>
        <div className="grid gap-2">
          <Label htmlFor="city">Kota</Label>
          <Input id="city" {...form.register("city")} />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="concerns">Kebutuhan utama</Label>
          <Textarea id="concerns" className="min-h-24" {...form.register("concerns")} />
        </div>
        <Button type="submit" disabled={form.formState.isSubmitting}>
          Buat akun
          <ArrowRight />
        </Button>
      </form>
    </AuthShell>
  );
}
